import React, { useCallback, useMemo, useEffect } from 'react'
import { useQueryParams } from '@fs/zion-router'
import { usePermission } from '@fs/zion-permissions'
import { Bleed, Button, InfoSheetPanel, Paragraph } from '@fs/zion-ui'
import { useLazyQuery } from '@apollo/client'
import { NoticeLoading } from '@fs/zion-icon'
import { useFeatureFlag } from '@fs/zion-flags'
import { GroupDataHeader, ImageOrganizeFooter, ImageOrganizeHeader, VolumeSetMetadata } from '../components'
import Coverages from './coverages/Coverages'
import Metadata from './metadata/Metadata'
import Internal from './internalFields/Internal'
import { useGroupData, useVolumeSetMetadata, useImageOrganizeModes, useImageOrganizeContext } from '../hooks'
import References from './references/References'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import ChangeHistoryPanel from './changeHistory/components/ChangeHistoryPanel/ChangeHistoryPanel'
import transl from './changeHistory/ChangeHistory.transl'
import queries from '../queries'
import getFormattedDate, { dateFormats } from '../../../../shared/utils/dateFormatHelper'
import style from './GroupData.styles'
import RedactionSection from '../../../../shared/components/redaction/RedactionSection'

const CameraOperatorFeedback = React.lazy(() => import('./cameraOperatorFeedback/CameraOperatorFeedback'))

export default function GroupData({ hideHeader }) {
  const [internalAuthorized, internalAuthorizedStatus] = usePermission(
    'LightyearInternalSearchPermission',
    'LightyearContext'
  )
  const { query } = useQueryParams()
  const [ImageRedactionPermission] = usePermission('RIP-Perm-ImageRedaction', 'RIP-Ctx-SpecialImageOps')
  const showRedactionLinkButton = useFeatureFlag('rip_sharedImageViewer_showRedactionLinkButton')
  const { grid } = query
  const { initialGroup = {} } = useGroupData()
  const { isImageOrganizeMode } = useImageOrganizeModes()
  const { hasEditPermissions } = useImageOrganizeContext()
  const { openOverlay, groupHistoryOverlay } = useOverlayContext()

  const volumeSetMetadata = useVolumeSetMetadata()
  const feedbackMode = query?.feedback
  const [getGroupHistory, { data: changeHistoryData, loading: loadChangeHistory }] = useLazyQuery(
    queries.getGroupChangeHistory()
  )

  const isVolumeSet = initialGroup?.types?.includes('VOLUME_SET')
  const isRestrictedGroup = initialGroup?.properties?.some((prop) => prop?.name === 'RESTRICTED_GROUP')

  const openEditPanel = useCallback(() => {
    openOverlay('editMetadataOverlay')
  }, [openOverlay])

  const Header = useCallback(() => {
    if (hideHeader) return null
    if (isImageOrganizeMode && !isVolumeSet)
      return <ImageOrganizeHeader hideEditButton={!hasEditPermissions} onEditClick={openEditPanel} />
    return <GroupDataHeader onEditClick={openEditPanel} hideEditButton={!hasEditPermissions} />
  }, [hasEditPermissions, hideHeader, isImageOrganizeMode, isVolumeSet, openEditPanel])

  const openHistory = useCallback(async () => {
    if (!changeHistoryData) await getGroupHistory({ variables: { groupId: query.groupId } })
    openOverlay('groupHistoryOverlay')
  }, [openOverlay, getGroupHistory, changeHistoryData, query.groupId])

  const lastChangedText = useMemo(() => {
    const lastChange = changeHistoryData?.getGroupHistory?.[0]
    if (!lastChange) return null

    const user = lastChange?.user?.contactName || transl.unknown()
    const date =
      getFormattedDate(
        lastChange?.date?.fromTimestamp,
        lastChange?.date?.toTimestamp,
        dateFormats.formattedFullWithTime
      ) || transl.unknown()

    return transl.lastChangedBy(date, user)
  }, [changeHistoryData])

  useEffect(() => {
    if (query.groupHistory && !changeHistoryData && query.groupId) openHistory()
  }, [query.groupHistory, query.groupId, openHistory, changeHistoryData])

  const hiddenFields = useMemo(() => {
    if (isRestrictedGroup) return ['citation']
    if (isVolumeSet) return ['archivalReferenceNumber']
    return []
  }, [isRestrictedGroup, isVolumeSet])

  return (
    <Bleed bottom>
      {isVolumeSet && <VolumeSetMetadata groupId={initialGroup.id} metadata={volumeSetMetadata} />}
      <Header />
      <Coverages />
      <References />
      <Metadata hiddenFields={hiddenFields} />
      {feedbackMode && (
        <React.Suspense fallback={null}>
          <CameraOperatorFeedback />
        </React.Suspense>
      )}
      {!isRestrictedGroup && (
        <div css={style.openChangeHistory}>
          <Paragraph size="sm">{lastChangedText}</Paragraph>
          <Button
            data-testid="openChangeHistory"
            emphasis="low"
            keyline="left"
            onClick={openHistory}
            disabled={loadChangeHistory}
            Icon={loadChangeHistory ? NoticeLoading : null}
          >
            {!changeHistoryData ? transl.loadChangeHistory() : transl.showHistoryButtonText()}
          </Button>
        </div>
      )}
      <InfoSheetPanel {...groupHistoryOverlay} title={transl.changeHistoryTitle()} subtitle={transl.changeHistorySub()}>
        <ChangeHistoryPanel changeHistory={changeHistoryData?.getGroupHistory || []} />
      </InfoSheetPanel>
      {showRedactionLinkButton?.isOn && ImageRedactionPermission && grid !== 'on' && <RedactionSection />}
      {internalAuthorized && internalAuthorizedStatus === 'loaded' && <Internal />}
      {isImageOrganizeMode && !isVolumeSet && <ImageOrganizeFooter />}
    </Bleed>
  )
}
